import {
  Button,
  FormGroup,
  InlineLink,
  Input,
  Label,
  NotifierNotification,
  UnsavedChangesPrompt,
} from "@mwi/ui";
import { useUpdatePracticeMutation } from "api";
import useErrorHandler from "hooks/useErrorHandler";
import useTitle from "hooks/useTitle";
import { useEffect } from "react";
import { useNotifier } from "react-headless-notifier";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { setPractice } from "slices/practice";
import { RootState } from "store";

type Inputs = {
  links: {
    title: string;
    url: string;
  }[];
};

const LinksPage = (): JSX.Element => {
  useTitle("Links");

  const { notify } = useNotifier();
  const dispatch = useDispatch();
  const errorHandler = useErrorHandler();

  const { practice } = useSelector((state: RootState) => ({
    practice: state.practice,
  }));

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isDirty },
  } = useForm<Inputs>({
    defaultValues: {
      links: practice.links?.length
        ? practice.links
        : [
            {
              title: "",
              url: "",
            },
          ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "links",
  });

  const [
    updatePractice,
    { isSuccess, isLoading, error, data: updatedPractice },
  ] = useUpdatePracticeMutation();

  useEffect(() => {
    if (error) {
      errorHandler(error, setError);
    }
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(setPractice(updatedPractice!));
      reset({
        links: updatedPractice?.links?.length
          ? updatedPractice.links
          : [{ title: "", url: "" }],
      });
      notify(
        <NotifierNotification
          title="Practice Updated"
          message="Practice links updated successfully"
        />,
      );
    }
  }, [isSuccess]);

  const handleUpdate = (data: Inputs) => {
    if (
      data.links?.length === 1 &&
      !data.links[0].title.trim() &&
      !data.links[0].url.trim()
    ) {
      data.links = [];
    }

    updatePractice({
      id: practice.id!,
      body: data,
    });
  };

  return (
    <UnsavedChangesPrompt isDirty={isDirty}>
      <form
        className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6 max-w-[588px]"
        onSubmit={handleSubmit(handleUpdate)}
      >
        <div className="flex flex-col gap-y-[2px] col-span-full mb-8 text-lg">
          <p className="font-semibold">
            Links to key documents of your practice
          </p>
          <p>
            Pet owners will be able to access these links from their account
          </p>
        </div>

        <div className="flex flex-col mb-12 gap-y-8 col-span-full">
          <InlineLink to="/terms" size="medium">
            Terms & Conditions Link
          </InlineLink>

          <InlineLink to="/privacy" size="medium">
            Privacy Policy Link
          </InlineLink>
        </div>

        <div className="flex flex-col gap-y-[2px] col-span-full mb-8 text-lg">
          <p className="font-semibold">Additional Links</p>
          <p>These custom links will also be available to pet owners</p>
        </div>

        <div className="mb-32 space-y-12 col-span-full">
          {fields.map((field, index) => (
            <div key={field.id} className="border-b-2 border-pinball">
              <FormGroup error={errors?.links?.[index]?.title?.message}>
                <Label>Title</Label>
                <Input size="medium" {...register(`links.${index}.title`)} />
              </FormGroup>

              <FormGroup error={errors?.links?.[index]?.url?.message}>
                <Label>URL</Label>
                <Input
                  size="medium"
                  type="url"
                  {...register(`links.${index}.url`)}
                />
              </FormGroup>

              {fields.length > 1 && (
                <div className="mb-2 text-right">
                  <InlineLink onClick={() => remove(index)} underline={false}>
                    Remove
                  </InlineLink>
                </div>
              )}
            </div>
          ))}

          <div className="mt-6">
            <InlineLink
              onClick={() =>
                append({
                  title: "",
                  url: "",
                })
              }
              underline={false}
            >
              + Add new link
            </InlineLink>
          </div>
        </div>

        <div className="col-span-full max-w-[185px]">
          <Button block type="submit" loading={isLoading}>
            Save changes
          </Button>
        </div>
      </form>
    </UnsavedChangesPrompt>
  );
};

export default LinksPage;
